import { STYTCH_MAX_SESSION_DURATION_MINUTES } from '@/utilities/stytch/config'

export const CLIENT_PORTAL_SESSION_LENGTH_FLAG = 'client-portal-session-length'
export const CLIENT_PORTAL_LOGIN_SESSION_MINUTES = 1440
export const CLIENT_PORTAL_FAILED_LOAD_SESSION_MINUTES = 60

// Stytch accepts a session from 5 minutes through STYTCH_MAX_SESSION_DURATION_MINUTES.
// The patient portal uses this same maximum.
const CLIENT_PORTAL_SESSION_MIN_MINUTES = 5
const CLIENT_PORTAL_SESSION_MAX_MINUTES = STYTCH_MAX_SESSION_DURATION_MINUTES

const PARTNERS_LOAD_FAILED_KEY = 'client-portal-session:partners-load-failed'

export type ClientPortalSessionPartner = {
  partnerId: string
  featureFlags?: readonly { name: string; value: unknown }[]
}

export type ClientPortalSessionDecision =
  | { type: 'wait' }
  | { type: 'skip' }
  | { type: 'remember'; partnerKey: string }
  | { type: 'authenticate'; minutes: number; partnerKey: string }

type AppliedClientPortalSession = {
  memberId: string
  partnerKey: string | null
  successfulMinutes: number | null
}

let appliedClientPortalSession: AppliedClientPortalSession | null = null

export function clearAppliedClientPortalSessionLength(): void {
  appliedClientPortalSession = null
}

export function readAppliedClientPortalSessionPartnerKey(memberId: string): string | null {
  if (appliedClientPortalSession?.memberId !== memberId) return null
  return appliedClientPortalSession.partnerKey
}

export function readAppliedClientPortalSessionMinutes(memberId: string): number | null {
  if (appliedClientPortalSession?.memberId !== memberId) return null
  return appliedClientPortalSession.successfulMinutes
}

export function rememberAppliedClientPortalSessionPartner(
  memberId: string,
  partnerKey: string,
  successfulMinutes?: number,
): void {
  const keptMinutes =
    appliedClientPortalSession?.memberId === memberId
      ? appliedClientPortalSession.successfulMinutes
      : null

  appliedClientPortalSession = {
    memberId,
    partnerKey,
    successfulMinutes: successfulMinutes ?? keptMinutes,
  }
}

export function recordAppliedClientPortalSessionSuccess(
  memberId: string,
  partnerKey: string,
  minutes: number,
): void {
  if (appliedClientPortalSession?.memberId !== memberId) return
  if (appliedClientPortalSession.partnerKey !== partnerKey) return
  appliedClientPortalSession = { memberId, partnerKey, successfulMinutes: minutes }
}

// A failed refresh clears the partner key. The last successful duration stays.
export function clearAppliedClientPortalSessionPartnerKey(memberId: string): void {
  if (appliedClientPortalSession?.memberId !== memberId) return
  appliedClientPortalSession = {
    memberId,
    partnerKey: null,
    successfulMinutes: appliedClientPortalSession.successfulMinutes,
  }
}

function toIntegerMinutes(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isInteger(value) ? value : null
  }

  if (typeof value !== 'string') return null

  const trimmed = value.trim()
  if (!/^\d+$/.test(trimmed)) return null

  const minutes = Number(trimmed)
  return Number.isInteger(minutes) ? minutes : null
}

export function guardClientPortalSessionMinutes(value: unknown): number {
  const minutes = toIntegerMinutes(value)
  if (
    minutes === null ||
    minutes < CLIENT_PORTAL_SESSION_MIN_MINUTES ||
    minutes > CLIENT_PORTAL_SESSION_MAX_MINUTES
  ) {
    return CLIENT_PORTAL_LOGIN_SESSION_MINUTES
  }

  return minutes
}

export function readClientPortalSessionLengthFlag(
  partners: readonly ClientPortalSessionPartner[],
  partnerId: string,
): unknown {
  const partner = partners.find((item) => item.partnerId === partnerId)
  const flag = partner?.featureFlags?.find(
    (item) => item.name === CLIENT_PORTAL_SESSION_LENGTH_FLAG,
  )
  return flag ? flag.value : undefined
}

function authenticateOnce(
  minutes: number,
  partnerKey: string,
  lastAppliedKey: string | null,
): ClientPortalSessionDecision {
  if (lastAppliedKey === partnerKey) return { type: 'skip' }
  return { type: 'authenticate', minutes, partnerKey }
}

export function decideClientPortalSessionRefresh(input: {
  memberId: string | null
  partnersSettled: boolean
  partnersLoadFailed: boolean
  partnerId: string | null
  partners: readonly ClientPortalSessionPartner[]
  lastAppliedKey: string | null
  lastSuccessfulMinutes: number | null
}): ClientPortalSessionDecision {
  if (!input.memberId || !input.partnersSettled) {
    return { type: 'wait' }
  }

  if (input.partnersLoadFailed) {
    return authenticateOnce(
      CLIENT_PORTAL_FAILED_LOAD_SESSION_MINUTES,
      PARTNERS_LOAD_FAILED_KEY,
      input.lastAppliedKey,
    )
  }

  if (!input.partnerId) {
    return { type: 'wait' }
  }

  if (input.lastAppliedKey === input.partnerId) {
    return { type: 'skip' }
  }

  const minutes = guardClientPortalSessionMinutes(
    readClientPortalSessionLengthFlag(input.partners, input.partnerId),
  )

  // Skip only when the live session is still the 1440 minute login length.
  // A failed refresh clears the partner key and keeps the last successful duration.
  const sessionIsLoginLength =
    input.lastSuccessfulMinutes === null ||
    input.lastSuccessfulMinutes === CLIENT_PORTAL_LOGIN_SESSION_MINUTES
  if (
    input.lastAppliedKey === null &&
    minutes === CLIENT_PORTAL_LOGIN_SESSION_MINUTES &&
    sessionIsLoginLength
  ) {
    return { type: 'remember', partnerKey: input.partnerId }
  }

  return { type: 'authenticate', minutes, partnerKey: input.partnerId }
}
